import { BACKEND_DATE_FORMATS, UTCMoment } from 'src/utils/UTCMoment';
import {
  DataAggregationDuration,
  ReadConfigurationDocument,
  ReadConfigurationQuery,
  ReadConfigurationQueryResult,
  ReadConfigurationQueryVariables,
  ReadProjectLocationVisibilityDocument,
  ReadProjectLocationVisibilityQuery,
  ReadProjectLocationVisibilityQueryVariables,
  ReadSimulationResultsDocument,
  ReadSimulationResultsQuery,
  ReadSimulationResultsQueryVariables,
  SimulationResultsData,
} from 'src/graphql';
import {
  appendSimulationResults,
  configurationReset,
  setActiveConfigurationJobUuid,
  setActiveConfigurationUuid,
  setConfiguration,
  setSimulationProgress,
  setSimulationResults,
  setSimulationStatus,
  updateConfigType,
  updateReadOnly,
} from 'src/redux/configuration/configuration.slice';
import { batch, useSelector } from 'react-redux';
import { matchPath, useHistory, useRouteMatch } from 'react-router';
import {
  selectActiveConfigurationJobUuid,
  selectActiveConfigurationUuid,
  selectAssetsValues,
  selectCommunityAsset,
  selectSelectedAssetUuid,
  selectSettingsData,
} from 'src/redux/configuration/configuration.selectors';
import { useCallback, useEffect } from 'react';

import { NN } from 'src/typings/helpers';
import { SimulationAreaResultsPartial } from 'src/graphql/subscriptions/simAreaResultsPartial';
import { TAsset } from 'src/typings/configuration.types';
import { destructureConfigTree } from 'src/utils/configuration/destructureConfigTree';
import { getCommunityAsset } from 'src/utils/configuration/getCommunityAsset';
import { parseSimulationResults } from 'src/utils/parseSimulationResults';
import { routesConfig } from 'src/routes/routes.config';
import { selectIsEmbed } from 'src/redux/application/application.selectors';
import { setCommunityNotFound } from 'src/redux/application/application.slice';
import { useApolloClient } from '@apollo/client';
import { useAppDispatch } from 'src/redux/store';
import { useAppLocation } from 'src/hooks/useAppLocation';
import { useConfigurationUtils } from 'src/hooks/useConfigurationUtils';
import { useSubscribeToQuery } from 'src/hooks/useSubscribeToQuery';

export type TSimulationResultData = ReturnType<typeof parseSimulationResults> & {
  assetUuid: TAsset['uuid'];
};

/**
 *
 *  __useConfigurationEffects__
 *
 *  This hook is important part of app flow. It's used for:
 *  - Fetching configuration on activeConfigurationUuid change
 *  - Parsing configuration and storing in redux
 *  - Fetching simulation results
 *  - Simulation subscription handling
 *  - Parsing and passing configuration results to redux
 *  - Catching route change, matching and setting activeConfigurationUuid
 *
 */
export function useConfigurationEffects(): void {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const history = useHistory();
  const location = useAppLocation();
  const isEmbed = useSelector(selectIsEmbed);
  const activeConfigurationUuid = useSelector(selectActiveConfigurationUuid);
  const activeJobUuid = useSelector(selectActiveConfigurationJobUuid);
  const assetsValues = useSelector(selectAssetsValues);
  const communityAsset = useSelector(selectCommunityAsset);
  const selectedAssetUuid = useSelector(selectSelectedAssetUuid);
  const settingsData = useSelector(selectSettingsData);
  const { zoomIntoConfiguration, discardCurrentConfiguration } = useConfigurationUtils();

  /**
   *
   *  Use lazy query for simulation results fetch. Using onCompleted is safer than handling in useEffect
   *
   */
  useSubscribeToQuery<ReadSimulationResultsQuery, ReadSimulationResultsQueryVariables>({
    queryDoc: ReadSimulationResultsDocument,
    subscriptionDoc: SimulationAreaResultsPartial,
    variables: {
      jobId: activeJobUuid || '',
      uuid: selectedAssetUuid || '',
      startTime: UTCMoment.fromBackend(
        settingsData.startDate,
        BACKEND_DATE_FORMATS.SETTINGS_DATA,
      ).format(BACKEND_DATE_FORMATS.SIMULATION_RESULTS_START_END_TIME),
      endTime: UTCMoment.fromBackend(settingsData.endDate, BACKEND_DATE_FORMATS.SETTINGS_DATA)
        .set({ hour: 23, minute: 59, second: 59 })
        .format(BACKEND_DATE_FORMATS.SIMULATION_RESULTS_START_END_TIME),
      // duration: DataAggregationDuration.Daily,
    },
    onQueryCompleted({ simulationResults }) {
      if (selectedAssetUuid) {
        updateSimulationResults(
          simulationResults as SimulationResultsData,
          selectedAssetUuid,
          'set',
        );
      }
    },
    updateQuery(prev, { subscriptionData }) {
      const resultsPartial = subscriptionData?.data
        .simulationAreaResultsPartial[0] as SimulationResultsData;

      if (selectedAssetUuid) {
        updateSimulationResults(resultsPartial, selectedAssetUuid, 'append');
      }

      return prev;
    },
  });

  /**
   *
   * Match route with map results path. It helps us to get configuration and asset uuids
   *
   */
  const paramsMatch = useRouteMatch<{ configurationUuid: string; assetUuid: string }>({
    path: isEmbed
      ? routesConfig.embed('')
      : routesConfig.singularityMapResults(undefined, undefined),
  });
  /**
   *
   *  __updateConfigurationStoreData__
   *
   *  Update configuration in redux store
   *  @param locationVisible Location visible param
   *  @param configurationData Parsed configuration data object
   *
   */
  const updateConfigurationStoreData = useCallback(
    (
      locationVisible,
      {
        jobUuid,
        configType,
        readOnly,
        name,
        description,
        project,
        assets,
        assetsValues,
        assetsTreeRelations,
        rootAssetUuid,
        selectedAssetUuid,
        settingsData,
      }: ReturnType<typeof parseConfigurationData>,
    ) => {
      batch(() => {
        dispatch(setActiveConfigurationJobUuid(jobUuid || undefined));
        dispatch(updateConfigType(configType || undefined));
        dispatch(updateReadOnly(!!readOnly));
        dispatch(
          setConfiguration({
            name: name || '',
            description: description || '',
            projectUuid: project?.uuid || '',
            locationVisible,
            assets,
            assetsTreeRelations,
            assetsValues,
            rootAssetUuid,
            selectedAssetUuid,
            // TODO: Type has to be fixed
            settingsData: settingsData || (undefined as any),
          }),
        );
      });
    },
    [dispatch],
  );

  /**
   *
   *  __updateSimulationResults__
   *
   *  Update simulation results in redux store
   *  @param result Simulation result object from backend
   *  @param assetUuid asset uuid
   *
   */
  const updateSimulationResults = useCallback(
    (result: SimulationResultsData, assetUuid: string, type: 'set' | 'append') => {
      const results = parseSimulationResults(result);

      batch(() => {
        dispatch(setSimulationStatus(results.status));
        dispatch(setSimulationProgress(results.progressInfo));

        if (results) {
          const actions = {
            set: setSimulationResults,
            append: appendSimulationResults,
          };

          dispatch(
            actions[type]({
              assetUuid,
              ...results,
            }),
          );
        }
      });
    },
    [dispatch],
  );

  /**
   *
   *  __parseConfigurationData__
   *
   *  Parse configuration object
   *  @param configuration Configuration object from backend
   *  @returns Parsed configuration data with destructured tree and communityAssetUuid
   *
   */
  function parseConfigurationData(
    configuration: NN<NN<ReadConfigurationQueryResult['data']>['readConfiguration']>,
    assetUuidFromUrl?: string,
  ) {
    const { scenarioData, simulationResults } = configuration;
    const jobUuid = simulationResults;
    const configTree = destructureConfigTree(scenarioData?.latest?.serialized);
    const { assets, assetsTreeRelations, rootAssetUuid } = configTree;
    const communityAssetUuid = getCommunityAsset({
      assets,
      rootAssetUuid,
      assetsTreeRelations,
    })?.uuid;
    return {
      ...configuration,
      ...configTree,
      jobUuid,
      selectedAssetUuid: assetUuidFromUrl || communityAssetUuid,
    };
  }

  /**
   *
   *  __callForConfiguration__
   *
   *  Read configuration data from backend
   *  @param uuid Configuration uuid
   *  @returns Configuration object
   *
   */
  const callForConfiguration = useCallback(
    async (uuid: string) => {
      const configurationResult = await client.query<
        ReadConfigurationQuery,
        ReadConfigurationQueryVariables
      >({
        query: ReadConfigurationDocument,
        variables: { uuid },
      });

      return configurationResult.data.readConfiguration;
    },
    [client],
  );

  /**
   *
   *  __callForProjectLocationVisibility__
   *
   *  Read project visibility param from backend
   *  @param projectUuid Project uuid
   *  @returns Boolean value of locationVisible param
   *
   */
  const callForProjectLocationVisibility = useCallback(
    async (projectUuid: string) => {
      const projectResult = await client.query<
        ReadProjectLocationVisibilityQuery,
        ReadProjectLocationVisibilityQueryVariables
      >({ query: ReadProjectLocationVisibilityDocument, variables: { projectUuid } });

      return projectResult?.data?.readProject?.locationVisible ?? true;
    },
    [client],
  );

  /**
   *
   *  __getConfiguration__
   *
   *  Main function for configuration reading.
   *  @param configurationUuid Uuid of configuration
   *
   */
  const getConfiguration = useCallback(
    async (configurationUuid) => {
      const configuration = await callForConfiguration(configurationUuid);

      if (configuration) {
        const configurationData = parseConfigurationData(
          configuration,
          paramsMatch?.params.assetUuid,
        );
        const { project, selectedAssetUuid, assetsValues } = configurationData;

        if (project?.uuid && selectedAssetUuid) {
          const locationVisible = await callForProjectLocationVisibility(project.uuid);

          updateConfigurationStoreData(locationVisible, configurationData);

          const routeMatch = matchPath(location.pathname, {
            path: isEmbed
              ? routesConfig.embed(activeConfigurationUuid)
              : routesConfig.singularityMapResults(activeConfigurationUuid, selectedAssetUuid),
          });

          if (!routeMatch?.isExact) {
            history.push(
              isEmbed
                ? routesConfig.embed(activeConfigurationUuid)
                : routesConfig.singularityMapResults(activeConfigurationUuid, selectedAssetUuid),
            );
          }

          zoomIntoConfiguration({ assetsValues });
        }
      }
    },
    [
      callForConfiguration,
      paramsMatch?.params.assetUuid,
      callForProjectLocationVisibility,
      updateConfigurationStoreData,
      location.pathname,
      isEmbed,
      activeConfigurationUuid,
      zoomIntoConfiguration,
      history,
    ],
  );

  /**
   *
   *  Fetch configuration and results after uuid change. Reset if configuration fetching fails
   *
   */
  useEffect(() => {
    async function makeRequest() {
      if (activeConfigurationUuid) {
        try {
          await getConfiguration(activeConfigurationUuid);
        } catch (error) {
          if (!isEmbed) {
            discardCurrentConfiguration();
          } else {
            dispatch(setCommunityNotFound(true));
          }
        }
      }
    }

    makeRequest();
  }, [activeConfigurationUuid, discardCurrentConfiguration, dispatch, getConfiguration, isEmbed]);

  /**
   *
   *  Get and set configuration uuid from URL
   *
   *  Notes:
   *  - setActiveConfigurationUuid dispatch will trigger readConfigurationQuery
   *
   */
  useEffect(() => {
    if (paramsMatch?.params.configurationUuid) {
      dispatch(setActiveConfigurationUuid(paramsMatch?.params.configurationUuid));
    }
  }, [dispatch, history, paramsMatch]);

  /**
   *
   *  Zoom into community on select community asset
   *
   */
  useEffect(() => {
    if (selectedAssetUuid === communityAsset?.uuid && assetsValues) {
      zoomIntoConfiguration({ assetsValues });
    }
  }, [selectedAssetUuid, assetsValues, communityAsset, zoomIntoConfiguration]);

  /**
   *
   *  On configuration quit
   *
   */
  useEffect(() => {
    if (!activeConfigurationUuid) {
      dispatch(configurationReset());
    }
  }, [activeConfigurationUuid, dispatch]);
}
